import {
  TreeItemType,
  useCreateFolder,
  useDeleteFile,
  useMoveFile,
  useRenameFile,
  useUploadFile,
} from '@api';
import { useNotification } from '@common';
import { modals } from '@mantine/modals';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

type SaveUploadedFileParamsType = {
  locationId: string;
  parentId: string;
  fileUuid: string;
};

type CreateFolderParamsType = {
  folderName: string;
  parentId: string;
  locationId: string;
};

type RenameFileParamsType = {
  fileId: string;
  newName: string;
};

type MoveFileParamsType = {
  fileId: string;
  newParentId: string;
};

type UseFilesMutationsReturnType = {
  createFolder: (params: CreateFolderParamsType) => void;
  saveUploadedFile: (params: SaveUploadedFileParamsType) => void;
  moveFile: (params: MoveFileParamsType) => void;
  renameFile: (params: RenameFileParamsType) => void;
  deleteFile: (item: TreeItemType) => void;
};

export const useFilesMutations = (
  refetchFiles: () => void
): UseFilesMutationsReturnType => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const { mutate: deleteFileMutation } = useDeleteFile();
  const { mutate: renameFileMutation } = useRenameFile();
  const { mutate: moveFileMutation } = useMoveFile();
  const { mutate: createFolderMutation } = useCreateFolder();
  const { mutateAsync: uploadFile } = useUploadFile();

  const deleteFile = (treeItem: TreeItemType) => {
    const isFolder = treeItem.children !== null;
    const entityKey = isFolder
      ? ('DELETE_FILE_MODAL.ENTITY.FOLDER' as const)
      : ('DELETE_FILE_MODAL.ENTITY.FILE' as const);

    modals.openConfirmModal({
      title: t('DELETE_FILE_MODAL.TITLE', {
        interpolations: t(entityKey),
      }),
      labels: {
        confirm: t('DELETE_FILE_MODAL.LABELS.CONFIRM'),
        cancel: t('DELETE_FILE_MODAL.LABELS.CANCEL'),
      },
      confirmProps: {
        color: 'red',
      },
      onConfirm: () =>
        deleteFileMutation(
          { fileId: treeItem.id },
          {
            onError: (error: AxiosError) => {
              showError({
                title: 'NOTIFICATIONS.DELETE_FILE.ERROR.TITLE',
                message: 'NOTIFICATIONS.DELETE_FILE.ERROR.MESSAGE',
                error,
              });
            },
            onSuccess: () => refetchFiles(),
          }
        ),
    });
  };

  const renameFile = (params: RenameFileParamsType) => {
    const { fileId, newName } = params;
    renameFileMutation(
      { fileId, data: { name: newName } },
      {
        onError: (error: AxiosError) => {
          showError({
            title: 'NOTIFICATIONS.RENAME_FILE.ERROR.TITLE',
            message: 'NOTIFICATIONS.RENAME_FILE.ERROR.MESSAGE',
            error,
          });
        },
        onSuccess: () => refetchFiles(),
      }
    );
  };

  const moveFile = (params: MoveFileParamsType) => {
    const { fileId, newParentId } = params;
    moveFileMutation(
      { fileId, data: { targetFolderId: newParentId } },
      {
        onError: (error: AxiosError) => {
          showError({
            title: 'NOTIFICATIONS.MOVE_FILE.ERROR.TITLE',
            message: 'NOTIFICATIONS.MOVE_FILE.ERROR.MESSAGE',
            error,
          });
        },
        onSuccess: () => refetchFiles(),
      }
    );
  };

  const createFolder = (params: CreateFolderParamsType) => {
    const { folderName, parentId, locationId } = params;
    createFolderMutation(
      { data: { folderName, parentId, locationId } },
      {
        onError: (error: AxiosError) => {
          showError({
            title: 'NOTIFICATIONS.CREATE_FOLDER.ERROR.TITLE',
            message: 'NOTIFICATIONS.CREATE_FOLDER.ERROR.MESSAGE',
            error,
          });
        },
        onSuccess: () => refetchFiles(),
      }
    );
  };

  const saveUploadedFile = (params: SaveUploadedFileParamsType) => {
    const { parentId, fileUuid, locationId } = params;
    uploadFile(
      { data: { data: { uuid: fileUuid, parentId, locationId } } },
      {
        onError: (error: AxiosError) => {
          showError({
            title: 'NOTIFICATIONS.UPLOAD_FILE.ERROR.TITLE',
            message: 'NOTIFICATIONS.UPLOAD_FILE.ERROR.MESSAGE',
            error,
          });
        },
        onSuccess: () => refetchFiles(),
      }
    );
  };

  return {
    createFolder,
    saveUploadedFile,
    moveFile,
    renameFile,
    deleteFile,
  };
};
